import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { onErrorShowMessage } from '~src/errors';
import ProjectItem from '../ProjectItem/ProjectItem';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  fetchEditorProjects,
  fetchEditorProjectTypes,
  fetchFuneralServiceById,
} from '~graphql/controllers';
import useQueryString from '../useQueryString';
import { FuneralServicesSelectorModal } from '~src/components/FuneralServices';

const ProjectsPage = () => {
  const urlParams = new URLSearchParams(window.location.search);

  const [funeralService, setFuneralService] = useState();
  const [isLoadingCanvases, setIsLoadingCanvases] = useState(true);
  const [isLoadingProjectTypes, setIsLoadingProjectTypes] = useState(true);
  const [
    showFuneralServiceSelectorModal,
    setShowFuneralServiceSelectorModal,
  ] = React.useState(false);
  const [projectTypes, setProjectTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [after, setAfter] = useState('');
  const [filterProjectType, setFilterProjectType] = useQueryString(
    urlParams,
    'projectType'
  );
  const [filterFuneralService, setFilterFuneralService] = useQueryString(
    urlParams,
    'funeralService'
  );

  const parseTypesResult = (items) =>
    items.map((item) => ({
      value: item.objectId,
      label: item.name,
      isGlobal: item.isGlobal,
    }));

  useEffect(() => {
    // Avoid a visible smooth scroll on mount; jump to top only if needed
    if (window.scrollY !== 0 || window.scrollX !== 0) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, []);

  useEffect(() => {
    if (filterFuneralService) {
      fetchFuneralServiceById(filterFuneralService).then(
        (selectedFuneralService) => {
          setFuneralService(selectedFuneralService);
        }
      );
    }
  }, [filterFuneralService]);

  useEffect(() => {
    fetchEditorProjectTypes()
      .then((result) => {
        setProjectTypes(parseTypesResult(result));
        setIsLoadingProjectTypes(false);
      })
      .catch((err) => {
        console.error(err);
        onErrorShowMessage();
      });
  }, []);

  const fetchAllProjects = useCallback(() => {
    const filters = {};

    if (filterProjectType) {
      const [objectId, isGlobal = false] = filterProjectType.split('_');

      filters.filterProjectType = {
        objectId,
        isGlobal: JSON.parse(isGlobal),
      };
    }

    if (filterFuneralService) {
      filters.filterFuneralService = filterFuneralService;
    }

    fetchEditorProjects({ after, filters })
      .then((result) => {
        setItems((prevItems) => {
          const incoming = result.edges.map((item) => {
            const pages = item.editor_canvas_pages.edges
              ? item.editor_canvas_pages.edges
              : item.editor_canvas_pages;
            item.editor_canvas_pages = pages.sort(
              (a, b) => a.node.order - b.node.order
            );
            return item;
          });

          const existingIds = new Set(prevItems.map((item) => item.objectId));
          const merged = [...prevItems];

          incoming.forEach((item) => {
            if (!existingIds.has(item.objectId)) {
              merged.push(item);
            }
          });

          return merged;
        });
        setAfter(result.pageInfo.endCursor);
        setHasMore(result.pageInfo.hasNextPage);
        setIsLoadingCanvases(false);
      })
      .catch((err) => {
        console.error(err);
        onErrorShowMessage();
      });
  }, [after, filterProjectType, filterFuneralService]);

  useEffect(() => {
    fetchAllProjects();
  }, [filterProjectType, filterFuneralService]);

  let selectedProjectTypeId;
  if (filterProjectType) {
    selectedProjectTypeId = filterProjectType?.split('_')[0];
  }

  const selectedProjectType = projectTypes.find(
    (item) => item.value === selectedProjectTypeId
  );

  return (
    <div className="projects-content">
      <section className="projects-hero">
        <div className="projects-hero__headline">
          <h1>I tuoi progetti</h1>
          <p>Gestisci e riprendi i progetti per i tuoi clienti.</p>
        </div>
        <div className="projects-hero__actions">
          <a href="/?openCreate=1" data-navigo className="projects-hero__cta">
            Nuovo progetto
          </a>
        </div>
      </section>

      <section className="projects-filters">
        <div className="projects-filters__header">
          <h2>Filtri</h2>
          <button
            type="button"
            className="g-btn g-btn-outline g-btn-primary g-btn-size-md projects-filters__reset"
            disabled={!filterProjectType && !filterFuneralService}
            onClick={() => {
              setFilterFuneralService('');
              setFilterProjectType('');
              setItems([]);
              setAfter('');
              setHasMore(false);
              setIsLoadingCanvases(true);
            }}
          >
            Rimuovi filtri
          </button>
        </div>

        <div className="projects-filters__controls">
          <div className="projects-filters__group">
            <label htmlFor="selectProjectType">Tipologia</label>
            <Select
              isLoading={isLoadingProjectTypes}
              classNamePrefix="projects-select"
              name="selectProjectType"
              id="selectProjectType"
              placeholder="Seleziona tipologia di progetto"
              options={projectTypes}
              value={selectedProjectType || null}
              onChange={(e) => {
                if (e && e.value) {
                  setItems([]);
                  setAfter('');
                  setIsLoadingCanvases(true);
                  setHasMore(false);
                  setFilterProjectType(`${e.value}_${e.isGlobal || false}`);
                }
              }}
              noOptionsMessage={() =>
                isLoadingProjectTypes
                  ? 'Caricamento in corso...'
                  : 'Nessuna tipologia trovata'
              }
            />
          </div>

          <div className="projects-filters__group">
            <label>Servizio</label>
            <button
              type="button"
              className="g-btn g-btn-primary g-btn-size-md projects-filters__action"
              onClick={() => setShowFuneralServiceSelectorModal(true)}
            >
              {funeralService && filterFuneralService
                ? [funeralService.nome, funeralService.cognome].join(' ')
                : 'Cerca servizio'}
            </button>
          </div>
        </div>

        <FuneralServicesSelectorModal
          show={showFuneralServiceSelectorModal}
          onHide={() => setShowFuneralServiceSelectorModal(false)}
          onChange={(e) => {
            if (e && e.objectId) {
              setItems([]);
              setAfter('');
              setIsLoadingCanvases(true);
              setHasMore(false);
              setFilterFuneralService(e.objectId);
              setFuneralService(e);
              setShowFuneralServiceSelectorModal(false);
            }
          }}
        />
      </section>

      <section className="projects-list" id="projects-list">
        <InfiniteScroll
          dataLength={items.length}
          next={fetchAllProjects}
          hasMore={hasMore}
          className="projects-list__scroll"
          loader={
            <div className="projects-grid g-grid g-grid-cards">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={`skl-more-${i}`} className="projects-skeleton-card">
                  <div
                    className="projects-skeleton-thumb g-skeleton g-w-full"
                    style={{ height: 170 }}
                  />
                  <div className="projects-skeleton-meta">
                    <div
                      className="g-skeleton g-skeleton-line"
                      style={{ width: '68%' }}
                    />
                    <div
                      className="g-skeleton g-skeleton-line"
                      style={{ width: '42%' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          }
        >
          <div className="projects-grid g-grid g-grid-cards">
            {isLoadingCanvases &&
              Array.from({ length: 12 }).map((_, i) => (
                <div key={`skl-${i}`} className="projects-skeleton-card">
                  <div
                    className="projects-skeleton-thumb g-skeleton g-w-full"
                    style={{ height: 170 }}
                  />
                  <div className="projects-skeleton-meta">
                    <div
                      className="g-skeleton g-skeleton-line"
                      style={{ width: '66%' }}
                    />
                    <div
                      className="g-skeleton g-skeleton-line"
                      style={{ width: '38%' }}
                    />
                  </div>
                </div>
              ))}

            {!isLoadingCanvases && items.length === 0 && (
              <div className="col-12 projects-list__empty">
                <h3>Nessun progetto trovato. Vuoi crearne uno?</h3>
                <a
                  className="g-btn g-btn-primary projects-list__cta"
                  href={`/?openCreate=1${
                    selectedProjectTypeId
                      ? `&tipo=${selectedProjectTypeId}`
                      : ''
                  }${
                    filterFuneralService
                      ? `&servizio=${filterFuneralService}`
                      : ''
                  }`}
                  data-navigo
                >
                  Crea ora
                </a>
              </div>
            )}

            {!isLoadingCanvases &&
              items.map((project) => (
                <ProjectItem
                  key={project.objectId}
                  objectId={project.objectId}
                  name={`${project.defunto_pointer?.nome ?? ''} ${
                    project.defunto_pointer?.cognome ?? ''
                  }`.trim()}
                  metadata={project.metadata}
                  pagesPreview={project.editor_canvas_pages?.map(
                    (page) => page.node.preview?.url
                  )}
                  gifPreviewId={
                    project.editor_canvas_pages[0]?.node.gifPreviewId
                  }
                />
              ))}
          </div>
        </InfiniteScroll>
      </section>
    </div>
  );
};

export default ProjectsPage;
